import { createFileRoute, Link } from "@tanstack/react-router";
import { GraduationCap } from "lucide-react";
import { Button, Section } from "@alight/ui";
import { useSeo } from "../lib/seo";
import { useBlock } from "../lib/content";
import { CmsText } from "../components/CmsText";
import { PageIntro } from "../components/PageIntro";

export const Route = createFileRoute("/campus-time")({
  component: CampusTimePage,
});

function CampusTimePage() {
  const c = useBlock("campusTime");
  const locations = useBlock("locations");
  const banners = useBlock("banners");

  useSeo({
    title: "Campus Time | Connect Sydney",
    description:
      "Campus Time is where Sydney's Year 12 leavers meet the people heading to the same uni as them, and the staff workers from their campus Christian group — the final three Connect sessions, near Wynyard.",
    path: "/campus-time",
    image: banners.campusTimeImage,
  });

  return (
    <>
      <PageIntro
        eyebrow="The final three weeks"
        title="Campus Time"
        description={c.intro}
        image={banners.campusTimeImage}
        imageAlt={banners.campusTimeImageAlt}
      />

      <Section tone="paper" spacing="lg">
        <div className="grid items-start gap-10 lg:grid-cols-[1.2fr_1fr]">
          <div className="space-y-4 text-lg leading-relaxed text-muted">
            <CmsText text={c.body} />
            <div className="pt-4">
              <Button asChild>
                <Link to="/sessions">Campus Time dates</Link>
              </Button>
            </div>
          </div>
          <div className="rounded-card border border-border bg-card p-6 shadow-soft">
            <p className="text-sm font-bold uppercase tracking-wide text-primary">{locations.campusTimeTitle}</p>
            {locations.campusTimeImage && (
              <img
                src={locations.campusTimeImage}
                alt={locations.campusTimeImageAlt}
                width={1000}
                height={727}
                loading="lazy"
                className="mt-4 w-full rounded-card object-cover"
              />
            )}
            <p className="mt-4 font-heading text-xl font-bold text-ink">{locations.campusTimeChurch}</p>
            <p className="text-muted">{locations.campusTimeAddress}</p>
            <p className="mt-2 text-sm text-muted">{locations.campusTimeNote}</p>
            {locations.campusTimeMapUrl && (
              <Button asChild variant="outline" size="sm" className="mt-4">
                <a href={locations.campusTimeMapUrl} target="_blank" rel="noreferrer">
                  Open in Google Maps
                </a>
              </Button>
            )}
          </div>
        </div>
      </Section>

      <Section tone="card" spacing="lg">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-heading text-3xl font-extrabold text-ink">{c.partnersTitle}</h2>
          <p className="mt-4 text-muted">{c.partnersIntro}</p>
        </div>
        <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {c.partners.map((p, i) => (
            <li key={i} className="flex items-center gap-3 rounded-card border border-border bg-paper p-4">
              <span
                aria-hidden="true"
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-card bg-primary/10 text-primary"
              >
                <GraduationCap className="h-5 w-5" />
              </span>
              <span>
                <span className="block font-heading font-bold text-ink">{p.name}</span>
                <span className="block text-sm text-muted">{p.detail}</span>
              </span>
            </li>
          ))}
        </ul>
        <div className="mt-10 text-center">
          <Button asChild variant="outline">
            <Link to="/uni-christian-groups">Find your uni Christian group</Link>
          </Button>
        </div>
      </Section>
    </>
  );
}
